import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ShieldAlert, Users } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import { DemoBadge, ErrorBox, Spinner } from "@/components/Bits";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AcceptInvite() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, loadLeagues, setCurrentLeagueId } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/invitations/${encodeURIComponent(token)}`).then(({ data }) => setInvite(data)).catch((e) => setInvite({ valid: false, reason: errMsg(e) }));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/invitations/${encodeURIComponent(token)}/accept`);
      await loadLeagues();
      if (data.league_id) setCurrentLeagueId(data.league_id);
      nav(data.league_id ? `/ligues/${data.league_id}` : "/ligues/nouvelle", { replace: true });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!invite || user === null) return <AuthShell title="Invitation"><Spinner /></AuthShell>;
  if (!invite.valid)
    return (
      <AuthShell title="Invitation">
        <div data-testid="invite-invalid" className="mpl-card flex gap-3 p-5 text-sm">
          <ShieldAlert className="shrink-0 text-amber-300" size={22} />
          <div>
            <p className="font-semibold text-white">Invitation non valide</p>
            <p data-testid="invite-invalid-reason" className="mt-1 text-slate-400">{invite.reason}</p>
          </div>
        </div>
        <Link to={user ? "/tableau-de-bord" : "/"} className="mpl-btn-ghost mt-6 w-full">Retour</Link>
      </AuthShell>
    );

  return (
    <AuthShell title="Vous êtes invité" subtitle={invite.kind === "organizer" ? "Invitation organisateur : créez votre compte puis votre propre ligue." : invite.kind === "corp_owner" ? `Ma Petite Ligue vous confie l'organisation de « ${invite.league_name} » : connectez-vous ou créez votre compte avec l'adresse courriel invitée pour en devenir propriétaire.` : `${invite.owner_name} vous invite à rejoindre sa ligue privée.`}>
      <div className="mpl-card p-5" data-testid="invite-preview">
        <div className="flex items-center justify-between">
          <p className="mpl-eyebrow">Ligue privée</p>
          {invite.is_demo && <DemoBadge />}
        </div>
        <p data-testid="invite-league-name" className="mt-2 font-display text-3xl font-extrabold uppercase">{invite.league_name || "Organisateur"}</p>
        {invite.kind !== "organizer" && <p className="mt-2 flex items-center gap-2 text-sm text-slate-400">
          <Users size={15} /> {invite.member_count}{invite.max_members ? ` / ${invite.max_members}` : ""} membres
        </p>}
      </div>
      {error && <div className="mt-4"><ErrorBox message={error} /></div>}
      <div className="mt-6 space-y-3">
        {user ? (
          <button onClick={accept} disabled={busy} className="mpl-btn-primary w-full py-3" data-testid="invite-accept-button">
            {busy ? "Un instant…" : `Rejoindre en tant que ${user.display_name}`}
          </button>
        ) : (
          <>
            <Link to={`/inscription?invitation=${token}`} className="mpl-btn-primary w-full py-3" data-testid="invite-register-button">Créer mon compte</Link>
            <Link to={`/connexion?suite=/invitation/${token}`} className="mpl-btn-ghost w-full py-3" data-testid="invite-login-button">J'ai déjà un compte</Link>
          </>
        )}
      </div>
    </AuthShell>
  );
}
